import { gql } from 'graphql-request'
import { useState } from 'react'
import { useMutation } from '@hooks/graphql'
import { useFlashMessage } from '@components/FlashMessage'
import { Loader2 } from 'lucide-react'

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'

const MFA_DISABLE_MUTATION = gql`
  mutation mfaDisable($input: DisableInput!) {
    mfaDisable(input: $input) {
      errors {
        message
      }
    }
  }
`

const DisableMFAModal = ({ isOpen, onClose, onSuccess }) => {
  const [error, setError] = useState('')
  const { setFlashMessage } = useFlashMessage()

  const { mutate: disableMFA, isLoading } = useMutation({
    gqlMutation: MFA_DISABLE_MUTATION,
    onSuccess: (data) => {
      if (data?.mfaDisable?.errors?.length > 0) {
        setError(data.mfaDisable.errors.map(err => err.message).join(', '));
      } else {
        setFlashMessage('Multi-Factor Authentication has been disabled', 'success');
        onSuccess();
        onClose();
      }
    },
    onError: (error) => {
      console.error('Error disabling MFA:', error)
      setError('An error occurred while disabling MFA. Please try again.')
    }
  })

  const handleDisableMFA = () => {
    disableMFA({ input: {} })
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Disable Multi-Factor Authentication</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <DialogDescription className="space-y-3">
            <p className="font-semibold">Are you sure you want to disable MFA?</p>
            <p>
              Disabling multi-factor authentication will reduce the security of your account.
              Without MFA, your account will only be protected by your password.
            </p>
            <p>
              If someone gains access to your password, they will be able to access your account without
              the additional security layer that MFA provides.
            </p>
            <p className="font-semibold text-destructive">
              We strongly recommend keeping MFA enabled for optimal account security.
            </p>
          </DialogDescription>

          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDisableMFA}
            disabled={isLoading}
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isLoading ? "Disabling..." : "Disable MFA"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default DisableMFAModal 
